import * as yup from 'yup';
import { create } from 'zustand';
import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
import type { Nilable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';
import { addUrlProtocol } from '~/utilities/urlHelpers';
import type { RegistrationPage } from '~/graphql';
import type { RegistrationCustomizationsInheritedFromBrand } from '../Brand/types';
import {
  getPersistedRegistrationPageCustomizations,
  getRegistrationPageCustomizationsForPreview,
} from '../utilities/registrationPageUtilities';
import { getGeneratedValuesForBrand } from '../utilities/getGeneratedValuesForBrand';
import { useBrandStore } from '../Brand/BrandStore';

const resizeQueryString = '?image_crop_resized=1280x720&image_quality=100';

type SavedCustomizations = {
  description: string;
  eventThumbnailBakeryHashedId: string | null;
  eventThumbnailAlt: string | null;
  eventThumbnailUrl: string | null;
  registrationPageCustomizations: RegistrationPage;
  heroVideoHashedId: Nilable<string>;
};

interface State {
  isValid: () => boolean;
  changes: () => string[];
  hasOutstandingChanges: () => boolean;
  discardChanges: () => void;
  setSavedCustomizations: (customizations: SavedCustomizations) => void;
  savedDescription: string;
  descriptionPreview: string;
  updateDescriptionPreview: (description: string) => void;
  savedRegistrationPageCustomizations: RegistrationPage;
  registrationPageCustomizationsPreview: RegistrationCustomizationsInheritedFromBrand;
  updateRegistrationPageCustomizations: (changes: Partial<RegistrationPage>) => void;
  eventThumbnailUrlPreview: string | null | undefined;
  savedEventThumbnailUrl: string | null | undefined;
  setEventThumbnailUrl: (eventThumbnailUrl: string | null) => void;
  savedEventThumbnailBakeryHashedId: string | null;
  eventThumbnailBakeryHashedIdPreview: string | null;
  setEventThumbnailBakeryHashedId: (hashedId: string | null) => void;
  savedEventThumbnailAlt: string | null;
  eventThumbnailAltPreview: string | null;
  setEventThumbnailAltPreview: (alt: string) => void;
  isLoadingRegistrationCustomizations: boolean;
  hasLoadedRegistrationCustomizationsData: boolean;
  savedHeroVideoHashedId: Nilable<string>;
  heroVideoHashedIdPreview: Nilable<string>;
  setHeroVideoHashedIdPreview: (heroVideoHashedIdPreview: Nilable<string>) => void;
}

export const validationSchema = yup.object().shape({
  logoTargetUrl: yup.string().url().nullable().transform(addUrlProtocol),
  logoRedirectUrl: yup.string().url().nullable().transform(addUrlProtocol),
  customButtonUrl: yup.string().url().nullable().transform(addUrlProtocol),
});

export const useRegistrationCustomizationStore = create<State>()((set, get) => ({
  isValid: () => {
    const { customButtonUrl, logoTargetUrl, logoRedirectUrl } =
      get().registrationPageCustomizationsPreview;
    return validationSchema.isValidSync({ customButtonUrl, logoTargetUrl, logoRedirectUrl });
  },
  discardChanges: () =>
    set((state) => {
      const BrandStore = useBrandStore.getState();
      const registrationPageCustomizationsPreview = getRegistrationPageCustomizationsForPreview(
        BrandStore.brand,
        state.savedRegistrationPageCustomizations,
      );
      return {
        descriptionPreview: state.savedDescription,
        eventThumbnailAltPreview: state.savedEventThumbnailAlt,
        eventThumbnailUrlPreview: state.savedEventThumbnailUrl,
        eventThumbnailBakeryHashedIdPreview: state.savedEventThumbnailBakeryHashedId,
        registrationPageCustomizationsPreview,
      };
    }),
  setSavedCustomizations: ({
    description,
    eventThumbnailAlt,
    eventThumbnailUrl,
    eventThumbnailBakeryHashedId,
    registrationPageCustomizations,
    heroVideoHashedId,
  }) =>
    set(() => {
      const BrandStore = useBrandStore.getState();
      const brandWithGeneratedValues = getGeneratedValuesForBrand(BrandStore.brand);

      const registrationPageCustomizationsPreview = getRegistrationPageCustomizationsForPreview(
        brandWithGeneratedValues,
        registrationPageCustomizations,
      );

      const savedRegistrationPageCustomizations = getPersistedRegistrationPageCustomizations(
        brandWithGeneratedValues,
        registrationPageCustomizationsPreview,
      );

      return {
        savedDescription: description,
        descriptionPreview: description,
        savedEventThumbnailBakeryHashedId: eventThumbnailBakeryHashedId,
        eventThumbnailBakeryHashedIdPreview: eventThumbnailBakeryHashedId,
        savedEventThumbnailAlt: eventThumbnailAlt,
        eventThumbnailAltPreview: eventThumbnailAlt,
        eventThumbnailUrlPreview: eventThumbnailUrl,
        savedEventThumbnailUrl: eventThumbnailUrl,
        savedRegistrationPageCustomizations,
        registrationPageCustomizationsPreview,
        isLoadingRegistrationCustomizations: false,
        hasLoadedRegistrationCustomizationsData: true,
        savedHeroVideoHashedId: heroVideoHashedId,
        heroVideoHashedIdPreview: heroVideoHashedId,
      };
    }),
  savedDescription: '',
  descriptionPreview: '',
  updateDescriptionPreview: (descriptionPreview: string) =>
    set(() => {
      return { descriptionPreview };
    }),
  eventThumbnailUrlPreview: null,
  savedEventThumbnailUrl: null,
  setEventThumbnailUrl: (eventThumbnailUrlPreview: string) => {
    let url = eventThumbnailUrlPreview;

    if (isNotNil(url) && !url.includes(resizeQueryString)) {
      url = `${url}${resizeQueryString}`;
    }

    set(() => {
      return { eventThumbnailUrlPreview: url };
    });
  },
  savedEventThumbnailBakeryHashedId: null,
  eventThumbnailBakeryHashedIdPreview: null,
  setEventThumbnailBakeryHashedId: (eventThumbnailBakeryHashedIdPreview: string | null) =>
    set(() => {
      return { eventThumbnailBakeryHashedIdPreview };
    }),
  savedEventThumbnailAlt: '',
  eventThumbnailAltPreview: '',
  setEventThumbnailAltPreview: (eventThumbnailAltPreview: string) =>
    set(() => {
      return { eventThumbnailAltPreview };
    }),
  savedHeroVideoHashedId: '',
  heroVideoHashedIdPreview: '',
  setHeroVideoHashedIdPreview: (heroVideoHashedIdPreview) => {
    set(() => {
      return {
        heroVideoHashedIdPreview,
      };
    });
  },
  savedRegistrationPageCustomizations: {} as RegistrationPage,
  registrationPageCustomizationsPreview: {} as RegistrationCustomizationsInheritedFromBrand,
  updateRegistrationPageCustomizations: (changes) => {
    set((state) => {
      const BrandStore = useBrandStore.getState();
      let newCustomizations = { ...state.registrationPageCustomizationsPreview };

      Object.entries(changes).forEach(([key, value]) => {
        // Apply the updated customizations
        newCustomizations[key] = value;
      });

      newCustomizations = getRegistrationPageCustomizationsForPreview(
        BrandStore.brand,
        newCustomizations,
      );

      return {
        ...state,
        registrationPageCustomizationsPreview: newCustomizations,
      };
    });
  },
  hasOutstandingChanges: () => {
    const BrandStore = useBrandStore.getState();
    const savedChanges = {
      registrationPageCustomizations: get().savedRegistrationPageCustomizations,
      description: get().savedDescription,
      eventThumbnailAlt: get().savedEventThumbnailAlt,
      eventThumbnailBakeryHashedId: get().savedEventThumbnailBakeryHashedId,
      heroVideoHashedId: get().savedHeroVideoHashedId,
    };

    const pendingChanges = {
      registrationPageCustomizations: getPersistedRegistrationPageCustomizations(
        BrandStore.brand,
        get().registrationPageCustomizationsPreview,
      ),
      description: get().descriptionPreview,
      eventThumbnailAlt: get().eventThumbnailAltPreview,
      eventThumbnailBakeryHashedId: get().eventThumbnailBakeryHashedIdPreview,
      heroVideoHashedId: get().heroVideoHashedIdPreview,
    };

    return !isEqual(pendingChanges, savedChanges);
  },
  changes: () => {
    const savedChanges = {
      ...get().savedRegistrationPageCustomizations,
      description: get().savedDescription,
      eventThumbnailAlt: get().savedEventThumbnailAlt,
      eventThumbnailBakeryHashedId: get().savedEventThumbnailBakeryHashedId,
      heroVideoHashedId: get().savedHeroVideoHashedId,
    };

    const pendingChanges = {
      ...get().registrationPageCustomizationsPreview,
      description: get().descriptionPreview,
      eventThumbnailAlt: get().eventThumbnailAltPreview,
      eventThumbnailBakeryHashedId: get().eventThumbnailBakeryHashedIdPreview,
      heroVideoHashedId: get().heroVideoHashedIdPreview,
    };

    // heroImageBakeryHashedId and heroImageUnsplashUrl is not in savedChanges for the initial upload
    // but is for replacement uploads (replacing) so it's needed here for event
    // tracking, see onCompleted in Registration/SidePanelMenu.tsx
    return uniq([
      ...Object.keys(savedChanges),
      'heroImageBakeryHashedId',
      'heroImageUnsplashUrl',
    ]).reduce((acc: string[], key) => {
      const isChanged = !isEqual(pendingChanges[key], savedChanges[key]);
      if (isChanged) {
        acc.push(key);
      }
      return acc;
    }, []);
  },
  isLoadingRegistrationCustomizations: true,
  hasLoadedRegistrationCustomizationsData: false,
}));
